/**
 * Zustand des Monats je Apartment: Apartmentliste, Übertragungsstand des Apartments, Wechsel mit
 * Rückfrage (danach startet der Editor leer) und die einmalige Übergabe von Auswahl oder Tag.
 */
import { useMemo, useState } from 'react';
import { useEditorGuard } from './calendar/useEditorGuard';
import type { IsoRange } from './calendar/rangePickerModel';
import { summarizeTransmissions } from './transmissionStatus';
import type { useAvailabilityPage } from './useAvailabilityPage';

export interface MonthHandover {
  apartmentId: string;
  ranges: IsoRange[];
}

export function useAvailabilityMonth(
  page: ReturnType<typeof useAvailabilityPage>,
  handover: MonthHandover | null,
) {
  const { calendar, updateSearch } = page;
  const apartmentId = page.apartmentId!;
  const editor = useEditorGuard(apartmentId);
  const [resetToken, setResetToken] = useState(0);
  const [handoverUsed, setHandoverUsed] = useState(false);
  const apartment = calendar.data?.apartments.find((entry) => entry.propertyId === apartmentId);
  const summary = useMemo(
    () =>
      apartment ? { ...summarizeTransmissions([apartment]), stale: Boolean(calendar.error) } : null,
    [apartment, calendar.error],
  );
  const options = (calendar.data?.apartments ?? []).map((entry) => ({
    id: entry.propertyId,
    name: entry.name,
  }));
  // Übergeben wird einmal: nach Verwerfen oder Hin- und Rückwechsel kommt die alte Auswahl nicht wieder.
  const initialRanges = handoverUsed
    ? undefined
    : handover?.apartmentId === apartmentId
      ? handover.ranges
      : page.preselectedDay
        ? [{ from: page.preselectedDay, to: page.preselectedDay }]
        : undefined;
  return {
    apartmentId,
    editor,
    resetToken,
    summary,
    options: options.length > 0 ? options : [{ id: apartmentId, name: '…' }],
    initialRanges,
    markHandoverUsed: () => setHandoverUsed(true),
    /** Ein Wechsel verwirft offene Eingaben nur nach Rückfrage – dann startet der Editor leer. */
    change: (patch: Parameters<typeof updateSearch>[0]) =>
      editor.guard(
        null,
        () => updateSearch(patch),
        () => {
          setResetToken((token) => token + 1);
          updateSearch(patch);
        },
      ),
  };
}
